import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getDisplayError } from '../utils/errorHandler';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const STYLES = `
  .ara-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 32px 20px; border-bottom: 1px solid ${C.border};
  }
  .ara-title { font-size: 1.35rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .ara-subtitle { color: ${C.muted}; font-size: 0.82rem; margin-top: 4px; }
  .ara-btn-outline {
    padding: 8px 16px; background: transparent; border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.sub}; font-size: 0.8rem; font-weight: 600;
    cursor: pointer; transition: all .15s;
  }
  .ara-btn-outline:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .ara-btn-outline:disabled { opacity: .45; cursor: not-allowed; }
  .ara-btn-sm { padding: 5px 12px; font-size: 0.75rem; }

  .ara-body { padding: 24px 32px; }

  .ara-tabs {
    display: flex; border-bottom: 1px solid ${C.border}; margin-bottom: 24px;
  }
  .ara-tab {
    padding: 10px 20px 12px; background: none; border: none; cursor: pointer;
    font-size: 0.88rem; line-height: 1.3; text-align: left;
    border-bottom: 2px solid transparent; margin-bottom: -1px;
    transition: all .15s; color: ${C.muted}; font-weight: 500;
  }
  .ara-tab.active { color: ${C.gold}; font-weight: 700; border-bottom-color: ${C.gold}; }
  .ara-tab-sub { font-size: 0.7rem; font-weight: 400; color: ${C.muted}; }

  .ara-error {
    padding: 10px 14px; border-radius: 9px; margin-bottom: 16px;
    background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3);
    color: ${C.coral}; font-size: 0.82rem;
  }
  .ara-loading { padding: 40px; text-align: center; color: ${C.muted}; font-size: 0.88rem; }

  .ara-summary-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 24px;
  }
  .ara-kpi {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 12px;
    padding: 14px 16px;
  }
  .ara-kpi-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 6px; }
  .ara-kpi-value { font-size: 1.05rem; font-weight: 800; font-family: 'JetBrains Mono', monospace; color: ${C.text}; }
  .ara-kpi-count { font-size: 0.72rem; color: ${C.muted}; margin-top: 3px; }

  .ara-export-row { display: flex; justify-content: flex-end; margin-bottom: 12px; }

  .ara-bucket { margin-bottom: 28px; }
  .ara-bucket-title {
    display: flex; align-items: center; gap: 8px;
    font-size: 0.82rem; font-weight: 700; margin-bottom: 10px;
  }
  .ara-bucket-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .ara-bucket-count { font-weight: 400; color: ${C.muted}; }

  .ara-table-wrap {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 12px; overflow: hidden;
  }
  .ara-table { width: 100%; border-collapse: collapse; }
  .ara-table th {
    padding: 9px 14px; background: rgba(255,255,255,0.03); border-bottom: 1px solid ${C.border};
    font-size: 0.68rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase;
    letter-spacing: .06em; text-align: left; white-space: nowrap;
  }
  .ara-table td {
    padding: 11px 14px; border-bottom: 1px solid rgba(42,46,69,0.5);
    font-size: 0.82rem; color: ${C.sub};
  }
  .ara-table tbody tr:last-child td { border-bottom: none; }
  .ara-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .ara-cell-name { font-weight: 700; color: ${C.text}; }
  .ara-mono { font-family: 'JetBrains Mono', monospace; font-weight: 700; }
  .ara-days-badge {
    display: inline-block; padding: 2px 8px; border-radius: 6px;
    font-size: 0.73rem; font-weight: 700;
  }

  .ara-empty { display: flex; flex-direction: column; align-items: center; padding: 48px 24px; gap: 8px; }
  .ara-empty-icon { font-size: 2rem; }
  .ara-empty-text { font-size: 0.88rem; color: ${C.muted}; }
`;

const fmt = (n) => {
  const num = Number(n || 0);
  return `GHS ${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const BUCKET_LABELS = [
  { key: 'current', label: '0–30 days',  color: C.teal,  bg: 'rgba(0,201,177,0.12)',   border: 'rgba(0,201,177,0.3)'   },
  { key: 'days30',  label: '31–60 days', color: C.gold,  bg: 'rgba(240,180,41,0.12)',  border: 'rgba(240,180,41,0.3)'  },
  { key: 'days60',  label: '61–90 days', color: '#FF8C6B', bg: 'rgba(255,107,107,0.1)', border: 'rgba(255,107,107,0.25)' },
  { key: 'days90',  label: '90+ days',   color: C.coral, bg: 'rgba(255,107,107,0.15)', border: 'rgba(255,107,107,0.35)' },
];

function exportCSV(rows, filename, isAR) {
  if (!rows.length) return;
  const headers = isAR
    ? ['Customer', 'Phone', 'Email', 'Invoice Amount', 'Paid', 'Outstanding', 'Due Date', 'Days Overdue', 'Status']
    : ['Supplier', 'Order Code', 'Phone', 'Email', 'Order Total', 'Paid', 'Outstanding', 'Expected Date', 'Days Past Due', 'Bill Status'];
  const csv = [
    headers.join(','),
    ...rows.map((r) => isAR
      ? [r.customerName, r.phone, r.email, r.amount, r.amountPaid, r.outstanding, fmtDate(r.dueDate), r.daysOverdue, r.status].join(',')
      : [r.supplierName, r.orderCode, r.phone, r.email, r.total, r.amountPaid, r.outstanding, fmtDate(r.expectedDate), r.daysPastDue, r.billStatus].join(',')
    ),
  ].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
}

function SummaryCards({ summary }) {
  if (!summary) return null;
  return (
    <div className="ara-summary-grid">
      {BUCKET_LABELS.map(({ key, label, color }) => (
        <div key={key} className="ara-kpi" style={{ borderLeftColor: color, borderLeftWidth: 3 }}>
          <div className="ara-kpi-label" style={{ color }}>{label}</div>
          <div className="ara-kpi-value">{fmt(summary[key]?.total)}</div>
          <div className="ara-kpi-count">{summary[key]?.count || 0} record{summary[key]?.count !== 1 ? 's' : ''}</div>
        </div>
      ))}
      <div className="ara-kpi" style={{ borderLeftColor: C.sky, borderLeftWidth: 3 }}>
        <div className="ara-kpi-label" style={{ color: C.sky }}>Total</div>
        <div className="ara-kpi-value">{fmt(summary.grandTotal)}</div>
      </div>
    </div>
  );
}

function AgingTable({ buckets, isAR, onExportAll }) {
  const allRows = BUCKET_LABELS.flatMap(({ key }) => buckets?.[key] || []);
  if (!allRows.length) return (
    <div className="ara-empty">
      <div className="ara-empty-icon">{isAR ? '🎉' : '✅'}</div>
      <div className="ara-empty-text">{isAR ? 'No outstanding receivables.' : 'No outstanding payables.'}</div>
    </div>
  );

  return (
    <div>
      <div className="ara-export-row">
        <button className="ara-btn-outline ara-btn-sm" onClick={onExportAll}>Export CSV</button>
      </div>
      {BUCKET_LABELS.map(({ key, label, color, bg, border }) => {
        const rows = buckets?.[key] || [];
        if (!rows.length) return null;
        return (
          <div key={key} className="ara-bucket">
            <div className="ara-bucket-title" style={{ color }}>
              <span className="ara-bucket-dot" style={{ background: color }} />
              {label}
              <span className="ara-bucket-count">({rows.length})</span>
            </div>
            <div className="ara-table-wrap">
              <table className="ara-table">
                <thead>
                  <tr>
                    {isAR ? (
                      <>
                        <th>Customer</th>
                        <th>Contact</th>
                        <th>Invoice</th>
                        <th>Paid</th>
                        <th>Outstanding</th>
                        <th>Due Date</th>
                        <th>Days Overdue</th>
                        <th>Status</th>
                      </>
                    ) : (
                      <>
                        <th>Supplier</th>
                        <th>Order</th>
                        <th>Order Total</th>
                        <th>Paid</th>
                        <th>Outstanding</th>
                        <th>Expected</th>
                        <th>Days Past Due</th>
                        <th>Bill Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      {isAR ? (
                        <>
                          <td><span className="ara-cell-name">{r.customerName}</span></td>
                          <td>{r.phone || r.email || '—'}</td>
                          <td><span className="ara-mono">{fmt(r.amount)}</span></td>
                          <td><span className="ara-mono">{fmt(r.amountPaid)}</span></td>
                          <td><span className="ara-mono" style={{ color }}>{fmt(r.outstanding)}</span></td>
                          <td>{fmtDate(r.dueDate)}</td>
                          <td>
                            <span className="ara-days-badge" style={{ background: bg, color, border: `1px solid ${border}` }}>
                              {r.daysOverdue}d
                            </span>
                          </td>
                          <td>
                            <span className="ara-days-badge" style={{
                              background: r.status === 'outstanding' ? 'rgba(240,180,41,0.12)' : 'rgba(56,189,248,0.1)',
                              color: r.status === 'outstanding' ? C.gold : C.sky,
                              border: `1px solid ${r.status === 'outstanding' ? 'rgba(240,180,41,0.3)' : 'rgba(56,189,248,0.25)'}`,
                            }}>{r.status}</span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td><span className="ara-cell-name">{r.supplierName}</span></td>
                          <td>{r.orderCode}</td>
                          <td><span className="ara-mono">{fmt(r.total)}</span></td>
                          <td><span className="ara-mono">{fmt(r.amountPaid)}</span></td>
                          <td><span className="ara-mono" style={{ color }}>{fmt(r.outstanding)}</span></td>
                          <td>{fmtDate(r.expectedDate)}</td>
                          <td>
                            <span className="ara-days-badge" style={{ background: bg, color, border: `1px solid ${border}` }}>
                              {r.daysPastDue}d
                            </span>
                          </td>
                          <td>
                            <span className="ara-days-badge" style={{
                              background: r.billStatus === 'unpaid' ? 'rgba(255,107,107,0.12)' : 'rgba(240,180,41,0.12)',
                              color: r.billStatus === 'unpaid' ? C.coral : C.gold,
                              border: `1px solid ${r.billStatus === 'unpaid' ? 'rgba(255,107,107,0.3)' : 'rgba(240,180,41,0.3)'}`,
                            }}>{r.billStatus}</span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ARAPAging() {
  const [tab, setTab]       = useState('ar');
  const [arData, setArData] = useState(null);
  const [apData, setApData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const tok = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${tok}` };

  const loadAR = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/reports/ar-aging`, { headers });
      const d = await r.json();
      if (!r.ok) throw { error: d.error };
      setArData(d);
    } catch (e) {
      console.error('[ARAPAging] AR load error:', e);
      setError(getDisplayError(e, 'Failed to load accounts receivable. Please try again.'));
    }
    finally { setLoading(false); }
  }, []);

  const loadAP = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/reports/ap-aging`, { headers });
      const d = await r.json();
      if (!r.ok) throw { error: d.error };
      setApData(d);
    } catch (e) {
      console.error('[ARAPAging] AP load error:', e);
      setError(getDisplayError(e, 'Failed to load accounts payable. Please try again.'));
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'ar' && !arData) loadAR(); }, [tab]);
  useEffect(() => { if (tab === 'ap' && !apData) loadAP(); }, [tab]);

  const handleExportAll = () => {
    if (tab === 'ar' && arData) {
      const all = BUCKET_LABELS.flatMap(({ key }) => arData.buckets?.[key] || []);
      exportCSV(all, 'ar-aging.csv', true);
    } else if (apData) {
      const all = BUCKET_LABELS.flatMap(({ key }) => apData.buckets?.[key] || []);
      exportCSV(all, 'ap-aging.csv', false);
    }
  };

  const data = tab === 'ar' ? arData : apData;

  return (
    <Layout>
      <style>{STYLES}</style>

      <div className="ara-header">
        <div>
          <h1 className="ara-title">Aging Reports</h1>
          <p className="ara-subtitle">Track outstanding receivables and payables by age</p>
        </div>
        <button
          className="ara-btn-outline"
          onClick={tab === 'ar' ? loadAR : loadAP}
          disabled={loading}
        >
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      <div className="ara-body">
        <div className="ara-tabs">
          {[
            { key: 'ar', label: 'Accounts Receivable', sub: 'What customers owe you' },
            { key: 'ap', label: 'Accounts Payable',    sub: 'What you owe suppliers' },
          ].map(({ key, label, sub }) => (
            <button key={key} className={`ara-tab${tab === key ? ' active' : ''}`} onClick={() => setTab(key)}>
              {label}
              <div className="ara-tab-sub">{sub}</div>
            </button>
          ))}
        </div>

        {error && <div className="ara-error">{error}</div>}
        {loading && !data && <div className="ara-loading">Loading aging data…</div>}

        {data && (
          <>
            <SummaryCards summary={data.summary} />
            <AgingTable buckets={data.buckets} isAR={tab === 'ar'} onExportAll={handleExportAll} />
          </>
        )}
      </div>
    </Layout>
  );
}
